import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { DateService } from '@app/shared/services/date.service'
import { pick } from 'lodash-es'


@Component({ selector: 'app-invitation-calendar', templateUrl: './invitation-calendar.component.html', styleUrls: ['./invitation-calendar.component.scss'], standalone: false })
export class InvitationCalendarComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef)
  /** Model dates */
  @Input() model: any = {}

  /** When model changed emit "modelChange" output  */
  @Output() modelChange = new EventEmitter<any>()

  /** Only starting date is required */
  @Input() onlyStartingDateIsRequired: boolean

  /** Label */
  @Input() label: string = 'VISITORS.INVITATION.VISIT_DATE'

  /** First date placeholder */
  @Input() firstDatePlaceholder: string = 'VISITORS.PLACEHOLDERS.ENTER_DATE'

  /** Second date placeholder */
  @Input() secondDatePlaceholder: string = 'VISITORS.PLACEHOLDERS.EXIT_DATE'

  /** Is date restriction */
  @Input() dateRestrictions: boolean = true

  /** First date form control name */
  @Input() firstDateFormControlName: string = 'starting_date'

  /** Second date form control name */
  @Input() secondDateFormControlName: string = 'ending_date'

  /** Input class */
  @Input() class?: string = 'p-input-filled p-input-icon-right fg-1'

  /** Disabled input? */
  @Input() disabled?: boolean

  /** Readonly input? */
  @Input() readonly?: boolean

  /** Label class */
  @Input() labelClass?: string

  /** Input tab index */
  @Input() tabindex: string | number | null = null

  /** Show the calendar on focus?  */
  @Input() showOnFocus: boolean = true

  /**
   * Fill an empty end date with the start date (the create drawers' default).
   * Done here, in the calendar's own form, and only once a date is picked or a field is left — never
   * while a key is typed. The drawers used to write this default back through [model], and the
   * ngOnChanges that followed reset the form under the operator's fingers: every key typed into the
   * end field put the start date back (YB-2197).
   */
  @Input() defaultEndToStart: boolean = false

  /** Form dates group */
  formDates: FormGroup

  /** Form group times */
  /*   formTimes: FormGroup;
   */
  uniq = 'id' + Math.random().toString(16).slice(2)

  /** Min date for choose only from date now and upper */
  minDate: Date


  //uniq = 'id' + Math.random().toString(16).slice(2);
  constructor(private fb: FormBuilder, private dateService: DateService) {}

  ngOnInit(): void {
    /* 
		if (this.dateRestrictions) {
      this.minDate = new Date(new Date(this.dateService.timezoneOffset(new Date(), null).startingDate))
    } */

    this.formBuild()

    this.formDates.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateDates()
    })

    this.formDates
      .get(this.secondDateFormControlName)!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.onlyStartingDateIsRequired) {
          if (value?.[this.secondDateFormControlName]) this.formDates?.get(this.secondDateFormControlName)!.addValidators(Validators.required)
          else this.formDates?.get(this.secondDateFormControlName)!.clearValidators()
          this.formDates?.get(this.secondDateFormControlName)!.updateValueAndValidity({ emitEvent: false })
        }
        this.updateDates()
      })
  }


  get completedAndInvalid() {
    return this.formDates.valid
  }

  get isInvalid() {
    if (!this.onlyStartingDateIsRequired || (this.onlyStartingDateIsRequired && this.model[this.secondDateFormControlName])) {
      if (!this.model[this.firstDateFormControlName] || !this.model[this.secondDateFormControlName]) {
        return true
      }

      const starting_date = new Date(this.model[this.firstDateFormControlName])
      const ending_date = new Date(this.model[this.secondDateFormControlName])

      if (starting_date > ending_date) {
        return true
      }

      const def1 = Math.abs((starting_date.getTime() - ending_date.getTime()) / 1000)
      if (def1 < 60) {
        return false
      }
    }
    return false
  }

  /**
   * Emit modelChanged output
   * @param data Data
   */
  toChange(data: any) {
    if (this.isInvalid) {
      data = {
        [this.firstDateFormControlName]: '',
        [this.secondDateFormControlName]: '',
      }
    }
    this.modelChange.emit(data)
  }

  /**
   * Update date, called every time model date/time changed
   */
  updateDates() {
    if (this.formDates.valid) {
      this.model = pick(this.formDates.value, this.firstDateFormControlName, this.secondDateFormControlName)
      this.toChange(this.model)
    } else {
      this.toChange({
        [this.firstDateFormControlName]: '',
        [this.secondDateFormControlName]: '',
      })
    }
  }

  /**
   * Put the start date into an empty end date when `defaultEndToStart` is on, and tell the parent.
   * Called when a start date is picked, and when either field is left.
   * @returns True when the end date was filled
   */
  fillEndFromStart(): boolean {
    if (!this.defaultEndToStart || !this.formDates) return false
    const start = this.formDates.get(this.firstDateFormControlName)!.value
    const end = this.formDates.get(this.secondDateFormControlName)!
    if (!(start instanceof Date) || isNaN(start.getTime()) || end.value) return false
    end.setValue(new Date(start), { emitEvent: false })
    this.updateDates()
    return true
  }

  /**
   * Set value to form method, called on init
   */
  setValueToForm() {
    if (this.formDates) {
      // Silent: loading a new model is not a change by the operator. The reset used to emit blank
      // dates to the parent on every [model] write, so a parent could not tell it from a range the
      // operator left incomplete or reversed (YB-2197).
      this.formDates.reset(undefined, { emitEvent: false })
      if (this.model?.[this.firstDateFormControlName]) {
        this.formDates.get(this.firstDateFormControlName)?.setValue(new Date(this.model[this.firstDateFormControlName]), {
          emitEvent: false,
        })
      }
      if (this.model?.[this.secondDateFormControlName]) {
        this.formDates.get(this.secondDateFormControlName)?.setValue(new Date(this.model[this.secondDateFormControlName]), {
          emitEvent: false,
        })
      }
    }
  }

  /**
   * Form build
   */
  formBuild() {
    this.formDates = this.fb.group({})
    this.formDates.addControl(this.firstDateFormControlName!, new FormControl({ value: '', disabled: !!this.disabled }, [Validators.required]))
    this.formDates.addControl(this.secondDateFormControlName!, new FormControl({ value: '', disabled: !!this.disabled }, []))

    if (!this.onlyStartingDateIsRequired) {
      this.formDates.get(this.secondDateFormControlName)!.setValidators(Validators.required)
      this.formDates.get(this.secondDateFormControlName)!.updateValueAndValidity()
    }

    if (Object.keys(this.model).length) {
      this.setValueToForm()
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      this.setValueToForm()
    }
    if (changes['onlyStartingDateIsRequired']) {
      this.updateFormValidation()
    }
    if (changes['disabled']) {
      this.updateDisabledState()
    }
  }

  updateFormValidation(): void {
    if (this.onlyStartingDateIsRequired) {
      this.formDates?.get(this.secondDateFormControlName)!.clearValidators()
    } else {
      this.formDates?.get(this.secondDateFormControlName)!.setValidators(Validators.required)
    }
    this.formDates?.get(this.secondDateFormControlName)!.updateValueAndValidity()
  }

  /**
   * Is field is required
   * @param key Form key
   * @returns True/false
   */
  isRequired(key: string): boolean {
    const control: AbstractControl | null = this.formDates?.get(key)
    if (!control) {
      return false
    }
    return !!(control.validator && !control.valid && !control.disabled)
  }

  /**
   * Update disabled state of form controls
   */
  private updateDisabledState(): void {
    if (!this.formDates) {
      return
    }

    const firstControl = this.formDates.get(this.firstDateFormControlName)
    const secondControl = this.formDates.get(this.secondDateFormControlName)

    if (this.disabled) {
      firstControl?.disable({ emitEvent: false })
      secondControl?.disable({ emitEvent: false })
    } else {
      firstControl?.enable({ emitEvent: false })
      secondControl?.enable({ emitEvent: false })
    }
  }
}
